/**
 * Backup Service
 * Handles desktop backup operations through backend API
 */

import i18n from '../../i18n';

const bk = (key, opts) => i18n.t(`serviceMessages.backup.${key}`, opts);

// Check if running in development or production
const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5001' 
  : `http://localhost:5001`; // In Electron, always use localhost:5001

const backupService = {
  /**
   * Get list of all backups
   * @returns {Promise<Array>} Array of backup objects
   */
  getBackups: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups`);
      const data = await response.json();
      
      if (data.success) {
        return data.backups;
      } else {
        throw new Error(data.error || bk('failed_get_backups'));
      }
    } catch (error) {
      console.error('Error getting backups:', error);
      throw error;
    }
  },

  /**
   * Get backup statistics
   * @returns {Promise<Object>} Backup statistics
   */
  getBackupStats: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/stats`);
      const data = await response.json();
      
      if (data.success) {
        return data.stats;
      } else {
        throw new Error(data.error || bk('failed_get_stats'));
      }
    } catch (error) {
      console.error('Error getting backup stats:', error);
      throw error;
    }
  },

  /**
   * Create a manual backup
   * @returns {Promise<Object>} Backup info
   */
  createBackup: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data.backup;
      } else {
        throw new Error(data.error || bk('failed_create'));
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      throw error;
    }
  },

  /**
   * Restore from a backup
   * @param {string} fileName - Backup file name
   * @returns {Promise<Object>} Restore result
   */
  restoreBackup: async (fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileName })
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || bk('failed_restore'));
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      throw error;
    }
  },

  /**
   * Verify backup integrity
   * @param {string} fileName - Backup file name
   * @returns {Promise<Object>} Verification result
   */
  verifyBackup: async (fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileName })
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || bk('failed_verify'));
      }
    } catch (error) {
      console.error('Error verifying backup:', error);
      throw error;
    }
  },

  /**
   * Delete a specific backup
   * @param {string} fileName - Backup file name
   * @returns {Promise<Object>} Delete result
   */
  deleteBackup: async (fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/${encodeURIComponent(fileName)}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || bk('failed_delete'));
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      throw error;
    }
  },

  /**
   * Export backup to external location
   * @param {string} fileName - Backup file to export
   * @param {string} destinationPath - Destination directory
   * @returns {Promise<Object>} Export result
   */
  exportBackup: async (fileName, destinationPath) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileName, destinationPath })
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || bk('failed_export'));
      }
    } catch (error) {
      console.error('Error exporting backup:', error);
      throw error;
    }
  },

  /**
   * Import backup from external location
   * @param {string} sourcePath - Source backup file path
   * @returns {Promise<Object>} Import result
   */
  importBackup: async (sourcePath) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sourcePath })
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || bk('failed_import'));
      }
    } catch (error) {
      console.error('Error importing backup:', error);
      throw error;
    }
  },

  /**
   * Start automatic backups
   * @param {string} frequency - 'daily', 'weekly', or 'monthly'
   * @returns {Promise<Object>} Result
   */
  startAutoBackup: async (frequency) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/auto/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ frequency })
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || bk('failed_start_auto'));
      }
    } catch (error) {
      console.error('Error starting auto backup:', error);
      throw error;
    }
  },

  /**
   * Stop automatic backups
   * @returns {Promise<Object>} Result
   */
  stopAutoBackup: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/backups/auto/stop`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || bk('failed_stop_auto'));
      }
    } catch (error) {
      console.error('Error stopping auto backup:', error);
      throw error;
    }
  }
};

export default backupService;

